import React, { useState, useEffect } from 'react';
import { BookOpen, Download, ExternalLink, Search, Filter, Brain } from 'lucide-react';
import QuizProgram from './QuizProgram';

interface Resource {
  id: string;
  title: string;
  description: string;
  image: string;
  downloadLink: string;
  category: string;
}

const resourcesData: { [key: string]: Resource[] } = {
  "Project Source Code": [
    {
      id: "Hero Section-1",
      title: "Hero Section Source Code",
      description: "Complete guide to work check read me file from GitHub, and review edit update concepts for beginners to advance.",
      image: "https://as2.ftcdn.net/jpg/05/14/79/43/1000_F_514794372_cZJc4gNTASOFs8MIwJouER5ZxfcT2Hyj.jpg",
      downloadLink: "https://github.com/arrowdropfoundation/HTML-TAGS.git",
      category: "Hero Section Tag"
    },
    {
      id: "Birthday Wishing App-2",
      title: "Birthday Website - Project Report and Source Code",
      description: "A heartfelt birthday website celebrating Rasheedha's 34th with wishes, timeline, music, and animations using React, Vite, and Tailwind.",
      image: "https://images.pexels.com/photos/2072181/pexels-photo-2072181.jpeg",
      downloadLink: "https://github.com/ARROW-DROP-FOUNDATION/Birthday-App-Source-Code.git",
      category: "React Programming"
    },
    {
      id: "Personal Portfolio App-3",
      title: "Personal Portfolio – Modern Developer Showcase",
      description: "A sleek, responsive portfolio website to present your skills, projects, resume, and contact info with a modern visual touch.",
      image: "https://images.stockcake.com/public/c/6/d/c6d73579-2432-4df5-8e8a-3d8a8cac1692_large/laptop-on-desk-stockcake.jpg",
      downloadLink: "https://github.com/arrowdropfoundation/Personal-Portfolio-Source-Code.git",
      category: "React Programming"
    }
  ],

  "Learning Skill Development": [
    {
      id: "Canva-Design-101",
      title: "Logo Design & Branding Mastery with Canva",
      description: "A complete self-learning course PDF covering Canva basics, logo creation, branding assets, export formats, platform guidelines, and client handling techniques. Ideal for beginners and aspiring designers.",
      image: "https://images.pexels.com/photos/4348404/pexels-photo-4348404.jpeg",
      downloadLink: "https://github.com/arrowdropfoundation/Canva-Skill-Development-PDF/archive/refs/heads/main.zip",
      category: "Design & Branding"
    },
    {
      id: "Money Management-101",
      title: "Money Management for Our Lives – How to Grow Our Lives",
      description: "Learn essential money management skills to build savings, avoid debt, invest wisely, and grow a secure, successful life.",
      image: "https://images.pexels.com/photos/4386397/pexels-photo-4386397.jpeg",
      downloadLink: "https://github.com/arrowdropfoundation/Money-Management.git",
      category: "Money Management"
    }
  ],
  
  "CNC Programming": [],
  "Web Development": [],
  "Blockchain Technology": [],
  "MS Office Suite": [],
  "Entrepreneurship": [],
  "Wireless Point to Point": [],
  "Blockchain Resources": [],
  "Wooden Work Resources": [],
  "Business Startup Resources": []
};

const TypingEffect: React.FC<{ text: string; speed?: number }> = ({ text, speed = 100 }) => {
  const [displayText, setDisplayText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (currentIndex < text.length) {
      const timeout = setTimeout(() => {
        setDisplayText(prev => prev + text[currentIndex]);
        setCurrentIndex(prev => prev + 1);
      }, speed);

      return () => clearTimeout(timeout);
    }
  }, [currentIndex, text, speed]);

  return (
    <span>
      {displayText}
      <span className="animate-pulse">|</span>
    </span>
  );
};

const ResourcesLibrary: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('Project Source Code');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSection, setActiveSection] = useState<'library' | 'quiz'>('library');

  // Set canonical URL for this page
  useEffect(() => {
    // Update canonical URL
    const canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      canonicalLink.setAttribute('href', 'https://arrowdropfoundation.com/resources');
    }

    // Update page title for SEO
    document.title = 'Resources Library - Arrow Drop Foundation | Free Learning Materials & Source Code';
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Access free learning resources, project source code, skill development materials, and educational content from Arrow Drop Foundation. Download PDFs, code repositories, and training materials.');
    }

    // Clean URL parameters that might cause duplicate content
    const url = new URL(window.location.href);
    const problematicParams = ['NA', 'MD', 'SD', 'SA', 'ND', 'MA'];
    let hasCleanedParams = false;
    
    problematicParams.forEach(param => {
      if (url.searchParams.has(param)) {
        url.searchParams.delete(param);
        hasCleanedParams = true;
      }
    });
    
    if (hasCleanedParams) {
      window.history.replaceState({}, document.title, url.pathname);
    }
  }, []);

  const categories = Object.keys(resourcesData);

  const filteredResources = resourcesData[selectedCategory]?.filter(resource =>
    resource.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    resource.description.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  const handleDownload = (downloadLink: string, title: string) => {
    // Track download for analytics
    if (typeof gtag !== 'undefined') {
      gtag('event', 'download', {
        'event_category': 'Resources',
        'event_label': title,
        'value': 1
      });
    }
    
    window.open(downloadLink, '_blank');
  };

  return (
    <section className="py-16 md:py-24 bg-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section with Typing Effect */}
        <div className="text-center mb-16 animated-section">
          <div className="mb-8 rounded-full bg-primary/10 p-4 inline-block">
            <BookOpen className="text-primary w-12 h-12 md:w-16 md:h-16" strokeWidth={2} />
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 max-w-4xl mx-auto">
            <TypingEffect 
              text="Welcome to Skill Development Library" 
              speed={80}
            />
          </h1>
          
          <div className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
            <TypingEffect 
              text="You can access study materials, projects, source code, CNC product development program code, and much more to enhance your skills and knowledge."
              speed={50}
            />
          </div>
        </div>

        {/* Section Navigation */}
        <div className="flex justify-center mb-12">
          <div className="flex gap-4 p-1 bg-muted rounded-lg">
            <button
              onClick={() => setActiveSection('library')}
              className={`flex items-center gap-2 px-6 py-3 rounded-md transition-colors ${
                activeSection === 'library'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <BookOpen className="w-5 h-5" />
              Resources Library
            </button>
            <button
              onClick={() => setActiveSection('quiz')}
              className={`flex items-center gap-2 px-6 py-3 rounded-md transition-colors ${
                activeSection === 'quiz'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Brain className="w-5 h-5" />
              Quiz Program
            </button>
          </div>
        </div>

        {/* Resources Library Section */}
        {activeSection === 'library' && (
          <>
            {/* Search and Filter */}
            <div className="max-w-4xl mx-auto mb-12">
              <div className="flex flex-col md:flex-row gap-4 mb-8">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search resources..."
                    className="w-full pl-10 pr-4 py-3 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Filter className="w-5 h-5 text-muted-foreground" />
                  <select
                    className="px-4 py-3 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                  >
                    {categories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Category Tabs */}
              <div className="flex flex-wrap gap-2 justify-center mb-8">
                {categories.map(category => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`px-4 py-2 rounded-full transition-colors text-sm ${
                      selectedCategory === category
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-card hover:bg-primary/10 border border-border'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>

            {/* Resources Grid */}
            <div className="max-w-6xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold mb-8 text-center">
                {selectedCategory} Resources
              </h2>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredResources.map((resource) => (
                  <div
                    key={resource.id}
                    className="bg-card rounded-lg overflow-hidden shadow-subtle dark:shadow-subtle-dark border border-border hover:shadow-lg transition-all duration-300 animated-section group"
                  >
                    <div className="aspect-video relative overflow-hidden">
                      <img
                        src={resource.image}
                        alt={resource.title}
                        className="object-cover w-full h-full group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-black/20 group-hover:bg-black/30 transition-colors duration-300"></div>
                    </div>
                    
                    <div className="p-6">
                      <h3 className="text-xl font-semibold mb-3 group-hover:text-primary transition-colors">
                        {resource.title}
                      </h3>
                      <p className="text-muted-foreground mb-4 line-clamp-3">
                        {resource.description}
                      </p>
                      
                      <div className="flex gap-3">
                        <button
                          onClick={() => handleDownload(resource.downloadLink, resource.title)}
                          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
                        >
                          <Download className="w-4 h-4" />
                          Download PDF / CODE
                        </button>
                        <button
                          onClick={() => window.open(resource.downloadLink, '_blank')}
                          className="p-2 border border-border rounded-md hover:bg-primary/10 transition-colors"
                          aria-label="View resource"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {filteredResources.length === 0 && (
                <div className="text-center py-12">
                  <BookOpen className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-2">No resources found</h3>
                  <p className="text-muted-foreground">
                    Try adjusting your search or select a different category.
                  </p>
                </div>
              )}
            </div>
          </>
        )}

        {/* Quiz Program Section */}
        {activeSection === 'quiz' && <QuizProgram />}
      </div>
    </section>
  );
};

export default ResourcesLibrary;